import {forEach, map, pickBy, reduce, assign, isNumber, mapKeys, merge, kebabCase, defaults, omitBy, get} from 'lodash'

import {
    Size,
    isUnitSize,
    isAutoSize,
    isKeywordSize,
    isAspectRatio,
    isMinMaxSize,
    isCalculationSize,
    ComponentLayout,
    LayoutDataItems,
    SingleLayoutData,
    Height,
    BaseItemLayout,
    GridContainerLayout,
    GridItemLayout,
    FlexContainerBase,
    FlexItemBase,
    GridBaseItem,
    ContainerBase,
    ScrollSnapAlign,
    ScrollSnap,
    ScrollBehaviour,
    Overflow
} from 'common-types/src/layoutData'
import {BaseDataItem} from 'common-types/src/structureTypes'
import {BrowserTypeFetcher} from 'common-types'

export interface CssObject {
    [property: string]: string
}

export interface SelectorToCssMap {
    [selector: string]: CssObject
}

export interface LayoutDataItemToCssOptions {
    browserType: BrowserTypeFetcher,
    renderSticky: boolean,
    wixAdsHeight: number,
    renderScrollSnap: boolean
}

const sizeToString = (size: Size | undefined | null): string => {
    if (!size) {
        return ''
    }

    if (isUnitSize(size)) {
        let type: string = size.type

        if (size.type === 'percentage') {
            type = '%'
        }

        return `${size.value}${type}`
    }

    if (isKeywordSize(size)) {
        let type: string = size.type

        switch (size.type) {
            case 'maxContent':
                type = 'max-content'
                break
            case 'minContent':
                type = 'min-content'
                break
        }
        return type
    }

    if (isMinMaxSize(size)) {
        return `minmax(${sizeToString(size.min)},${sizeToString(size.max)})`
    }

    if (isCalculationSize(size)) {
        return `calc(${size.value})`
    }

    throw new Error(`error parsing size: ${JSON.stringify(size)}`)
}

const alignmentToFlexSyntax = {
    start: 'flex-start' as 'flex-start',
    end: 'flex-end' as 'flex-end',
    center: 'center' as 'center',
    stretch: 'stretch' as 'stretch',
    auto: 'auto' as 'auto'
}

const justifyContentToFlexSyntax = {
    start: 'flex-start' as 'flex-start',
    end: 'flex-end' as 'flex-end',
    center: 'center' as 'center',
    spaceBetween: 'space-between' as 'space-between',
    spaceAround: 'space-around' as 'space-around'
}

export const itemPostfix = 'item'
export const containerPostfix = 'container'
export const overflowWrapperPostfix = 'overflow-wrapper'
export const containerWrapperPostfix = 'container-wrapper'
export const componentPostfix = 'component'

// todo commnent - explain - if it stays in
interface Hack {
    ___notaRealFields___: boolean
}

type TargetedCss = Hack & SelectorToCssMap
const setPrefix = (prefix: string) => (object: SelectorToCssMap) => mapKeys(object, (value, key) => `${prefix}${key}`) as TargetedCss
const setForOverflowWrapper = setPrefix(overflowWrapperPostfix)
const setForContainer = setPrefix(containerPostfix)
const setForContainerWrapper = setPrefix(containerWrapperPostfix)
const setForItem = setPrefix(itemPostfix)
const setForComponent = setPrefix(componentPostfix)
const mergeStyles = merge as (...args: TargetedCss[]) => TargetedCss
// const wrapWithRootObejct = (cssObj: CssObject) => ({
//     '': cssObj
// }) as SelectorToCssMap
// type anyFunction = (...args) => any
// const flow = (...args: anyFunction[]) => args.reduce((f, g) => (...innerArgs) => g(f(...innerArgs)))

type Margins = BaseItemLayout['margins']
type Padding = ContainerBase['padding']

const addKeyPrefixForCssObject = (keyPrefix, cssObject): CssObject => {
    const keys = Object.keys(cssObject)

    return keys.reduce((result, key) => {
        result[`${keyPrefix}${key}`] = sizeToString(cssObject[key])
        return result
    }, {})
}

const buildMargins = (margins: Partial<Margins>): CssObject => addKeyPrefixForCssObject('margin-', margins)

const buildPadding = (padding: Partial<Padding>): CssObject => addKeyPrefixForCssObject('padding-', padding)

const buildScrollSnapAlign = (scrollSnapAlign: ScrollSnapAlign): CssObject => ({
        'scroll-snap-align': scrollSnapAlign
    })

const buildSticky = <T extends BaseItemLayout>(itemLayout: T, {browserType, wixAdsHeight}: LayoutDataItemToCssOptions): CssObject => {
    const emptySize = sizeToString(null)
    const topString = itemLayout.top && wixAdsHeight && isUnitSize(itemLayout.top) ? `calc(${sizeToString(itemLayout.top)} + ${wixAdsHeight}px)` : sizeToString(itemLayout.top)
    return omitBy({
        position: browserType.safari ? '-webkit-sticky' : 'sticky',
        top: topString,
        bottom: sizeToString(itemLayout.bottom),
        left: sizeToString(itemLayout.left),
        right: sizeToString(itemLayout.right)
    }, value => value === emptySize)
}

const createItemLayoutConverter = <T extends BaseItemLayout>(specificItemLayoutConverter: (layout: T) => TargetedCss) => (itemLayout: T, options: LayoutDataItemToCssOptions) => {
    const specificStyles = specificItemLayoutConverter(itemLayout)
    const baseStyles = setForItem({
        '': Object.assign(
            itemLayout.position === 'sticky' && options.renderSticky ? buildSticky(itemLayout, options) : {position: 'relative'},
            itemLayout.margins ? buildMargins(itemLayout.margins) : {},
            itemLayout.scrollSnapAlign && options.renderScrollSnap ? buildScrollSnapAlign(itemLayout.scrollSnapAlign) : {})
    })
    return mergeStyles(baseStyles, specificStyles)
}

const buildScrollStyles = (
    scrollSnap: ScrollSnap,
    scrollBehaviour: ScrollBehaviour
  ): CssObject =>
    (
      {
        'scroll-snap-type':
          scrollSnap.scrollSnapType === 'none' ?
            scrollSnap.scrollSnapType :
            `${scrollSnap.scrollSnapDirection || ''}${
                scrollSnap.scrollSnapType ? ` ${scrollSnap.scrollSnapType}` : ''
              }`.trim(),
        '-webkit-scroll-snap-type': scrollSnap.scrollSnapType,
        'scroll-behavior': scrollBehaviour ? scrollBehaviour : 'auto'
      }
    )


const getOverflowStyles = (containerLayout, options) => {
    const overflowStyles = assign({},
        containerLayout.overflowX && {
            'overflow-x': containerLayout.overflowX
        },
        containerLayout.overflowY && {
            'overflow-y': containerLayout.overflowY
        }
    )

    const scrollSnapStyles = containerLayout.scrollSnap && containerLayout.scrollSnap.scrollSnapType && options.renderScrollSnap ? buildScrollStyles(containerLayout.scrollSnap, containerLayout.scrollBehaviour) : {}

    const hideScrollbarStyles = containerLayout.hideScrollbar ? {
        '': {
            'scrollbar-width': 'none',
            overflow: '-moz-scrollbars-none',
            '-ms-overflow-style': 'none'
        },
        '::-webkit-scrollbar': {
            width: '0',
            height: '0'
        }
    } : {}

    return merge({
        '': assign(overflowStyles, scrollSnapStyles)
    }, hideScrollbarStyles) as SelectorToCssMap
}
const createContainerLayoutConverter = <T extends ContainerBase>(specificItemLayoutConverter: (layout: T) => TargetedCss) => (containerLayout: T, options: LayoutDataItemToCssOptions) => {
    const specificStyles = specificItemLayoutConverter(containerLayout)

    const oneCellGrid = {
        '': {
            display: 'grid',
            ['grid-template-rows']: '1fr',
            ['grid-template-columns']: '1fr'
        }
    }

    const baseContainerLayout = {
        '': assign({
            'box-sizing': 'border-box'
        }, containerLayout.padding ? buildPadding(containerLayout.padding) : {})
    }

    const baseStyles = mergeStyles(
        setForContainer(baseContainerLayout),
        setForComponent(oneCellGrid),
        setForContainerWrapper(oneCellGrid),
        setForOverflowWrapper(oneCellGrid)
    )
    return mergeStyles(baseStyles, specificStyles, setForOverflowWrapper(getOverflowStyles(containerLayout, options)))
}

const parseBaseGridItem = <T extends GridBaseItem>(specificItemLayoutConverter: (layout: T) => TargetedCss) => (itemLayout: T, options: LayoutDataItemToCssOptions) => {
    const baseStyles = createItemLayoutConverter(specificItemLayoutConverter)(itemLayout, options)
    const additionalStyles: CssObject = {}

    if (itemLayout.alignSelf) {
        additionalStyles['align-self'] = itemLayout.alignSelf.toLowerCase()
    }

    if (itemLayout.justifySelf) {
        additionalStyles['justify-self'] = itemLayout.justifySelf.toLowerCase()
    }

    return mergeStyles(setForItem({'': additionalStyles}), baseStyles)
}

const parseFixedItem = parseBaseGridItem(() => setForItem({
        '': {
            'grid-area': '1/1/2/2',
            'pointer-events': 'auto'
        }
    })
)

/**
 * when parsing grid item we have a conflict in each axis between the size property and the position property
 * for example, width and justify - if justify is set to stretch, and we have a width, we don't know what to do
 * what will happen is we will render all properties and CSS will do its magic, in this case, the width property
 * is stronger than the justify property.
 */
const parseGridItem = parseBaseGridItem((gridItemLayout: GridItemLayout) => {
    const rootCss = {}

    const gridArea = gridItemLayout.gridArea
    // we might have partial GridItemLayout in case of extraction (sticky for example)
    if (gridArea) {
        const gridAreaString = `${gridArea.rowStart}/${gridArea.columnStart}/${gridArea.rowEnd}/${gridArea.columnEnd}`
        rootCss['grid-area'] = gridAreaString
    }

    return setForItem({'': rootCss})
})

const parseGridLayout = createContainerLayoutConverter((gridContainerLayout: GridContainerLayout) => {
    const rootCss: CssObject = {
        display: 'grid'
    }

    rootCss['grid-template-rows'] = gridContainerLayout.rows.map(sizeToString).join(' ')
    rootCss['grid-template-columns'] = gridContainerLayout.columns.map(sizeToString).join(' ')

    if (gridContainerLayout.rowGap) {
        rootCss['row-gap'] = sizeToString(gridContainerLayout.rowGap)
    }

    if (gridContainerLayout.columnGap) {
        rootCss['column-gap'] = sizeToString(gridContainerLayout.columnGap)
    }

    return setForContainer({'': rootCss})
})

const parseFlexContainerLayout = createContainerLayoutConverter((flexContainerLayout: FlexContainerBase) => mergeStyles(setForContainer({
    '': pickBy({
        display: 'flex',
        'flex-direction': kebabCase(flexContainerLayout.direction),
        'justify-content': justifyContentToFlexSyntax[flexContainerLayout.justifyContent],
        'align-items': alignmentToFlexSyntax[flexContainerLayout.alignItems],
        'flex-wrap': flexContainerLayout.wrap
    }, value => !!value)
})))

const parseFlexItemLayout = createItemLayoutConverter(<T extends FlexItemBase>(flexItemLayout: T) => {
    const rootCss: CssObject = {}

    if (flexItemLayout.alignSelf) {
        rootCss['align-self'] = alignmentToFlexSyntax[flexItemLayout.alignSelf]
    }

    if (isNumber(flexItemLayout.order)) {
        rootCss.order = String(flexItemLayout.order)
    }

    if (flexItemLayout.basis) {
        rootCss['flex-basis'] = sizeToString(flexItemLayout.basis)
    }

    if (isNumber(flexItemLayout.grow)) {
        rootCss['flex-grow'] = flexItemLayout.grow.toString()
    }

    if (isNumber(flexItemLayout.shrink)) {
        rootCss['flex-shrink'] = flexItemLayout.shrink.toString()
    }

    return setForItem({'': rootCss})
})

const getWidthCss = (width: Size) => (): SelectorToCssMap => setForComponent({
    '': {
        width: sizeToString(width)
    }
})

const getCompHeightCss = (height: Height) => {
    if (!height) {
        return {}
    }

    return isAspectRatio(height) ? {
        '': {
            height: 'auto'
        },
        '::before': {
            content: 'attr(x)',
            display: 'block',
            'padding-top': `${height.value * 100}%`
        }
    } : {
        '': {
            height: sizeToString(height as Size)
        }
    }
}

const getHeightCss = (height: Height) => (): SelectorToCssMap => {
    const compStyles = getCompHeightCss(height)

    const isOutsideIn = !!height && (isAspectRatio(height) || !isAutoSize(height))
    const containerWrapperStyles = isOutsideIn ? {
        '': {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%'
        }
    } : {
        '': {
            position: 'relative'
        }
    }

    const containerStyles = {
        '': {
            position: 'relative'
        }
    }
    return mergeStyles(
        setForComponent(compStyles),
        setForContainerWrapper(containerWrapperStyles),
        setForOverflowWrapper(containerWrapperStyles),
        setForContainer(containerStyles)
    )
}

const getMinWidthCss = (minWidth: Size) => (): SelectorToCssMap => setForComponent({
    '': {
        'min-width': sizeToString(minWidth)
    }
})

const getMinHeightCss = (minHeight: Size) => (): SelectorToCssMap => setForComponent({
    '': {
        'min-height': sizeToString(minHeight)
    }
})

const getMaxWidthCss = (maxWidth: Size) => (): SelectorToCssMap => setForComponent({
    '': {
        'max-width': sizeToString(maxWidth)
    }
})

const getMaxHeightCss = (maxHeight: Size) => (): SelectorToCssMap => setForComponent({
    '': {
        'max-height': sizeToString(maxHeight)
    }
})

const getRotationCss = rotationInDegrees => (): SelectorToCssMap => setForComponent({
    '': {
        transform: `rotate(${rotationInDegrees}deg)`
    }
})

const getHiddenCss = hidden => (): SelectorToCssMap => setForComponent(hidden ? {
    '': {
        display: 'none',
        visibility: 'hidden'
    }
} : {})

const getDirectionCss = direction => (): SelectorToCssMap => setForComponent(direction ? {
    '': {
        direction
    }
} : {})

// type PropertyConverter<T> = {
//     [P in keyof T]: (p: T[P]) => SelectorToCssMap
// }

const componentLayoutPropertiesConverters = {
    width: getWidthCss,
    height: getHeightCss,
    minWidth: getMinWidthCss,
    minHeight: getMinHeightCss,
    maxWidth: getMaxWidthCss,
    maxHeight: getMaxHeightCss,
    rotationInDegrees: getRotationCss,
    hidden: getHiddenCss,
    direction: getDirectionCss
}

const componentLayoutDefaults = {
    height: undefined
}

const componentLayoutConverter = (componentLayout: ComponentLayout) => {
    const componentLayoutWithDefaults = defaults({}, componentLayout, componentLayoutDefaults)
    const propertiesConverters = map(pickBy(componentLayoutWithDefaults, (val, key) => !!componentLayoutPropertiesConverters[key]), (val, key) => componentLayoutPropertiesConverters[key](val))

    return reduce(propertiesConverters, (selectorToCssMap, getCssFunc) => {
        const selectorToCssMapToAdd = getCssFunc()
        forEach(selectorToCssMapToAdd, (cssObj: CssObject, selector: string) => {
            selectorToCssMap[selector] = selectorToCssMap[selector] || {}
            assign(selectorToCssMap[selector], cssObj)
        })
        return selectorToCssMap as SelectorToCssMap
    }, {})
}


type PropsAsTypesOf<T extends {type: string}> = {
    readonly [P in T['type']]: ((item: BaseDataItem, layoutDataItemToCssOptions: LayoutDataItemToCssOptions) => TargetedCss);
}

const baseConverters: PropsAsTypesOf<LayoutDataItems> = {
    // @ts-ignore
    ComponentLayout: componentLayoutConverter,
    FixedItemLayout: parseFixedItem,
    GridItemLayout: parseGridItem,
    GridContainerLayout: parseGridLayout,
    FlexContainerLayout: parseFlexContainerLayout,
    FlexItemLayout: parseFlexItemLayout,
    OrganizerContainerLayout: parseFlexContainerLayout,
    OrganizerItemLayout: parseFlexItemLayout,
    StackContainerLayout: parseFlexContainerLayout,
    StackItemLayout: parseFlexItemLayout
}

const singleLayoutDataConverter = (item: SingleLayoutData, layoutDataItemToCssOptions: LayoutDataItemToCssOptions) => {
    const data = {}
    forEach(['componentLayout', 'itemLayout', 'containerLayout'], key => {
        const converter = baseConverters[get(item, [key, 'type'])]
        if (converter) {
            merge(data, converter(item[key], layoutDataItemToCssOptions))
        }
    })
    return data as TargetedCss
}

const converters: PropsAsTypesOf<LayoutDataItems> = {
    ...baseConverters,
    SingleLayoutData: singleLayoutDataConverter
}

export const layoutDataItemToCss = (layoutDataItem: LayoutDataItems, options: LayoutDataItemToCssOptions) => {
    const converter = converters[layoutDataItem.type]
    if (!converter) {
        throw new Error(`invalid layout type: ${layoutDataItem.type}`)
    }
    return converter(layoutDataItem, options) as SelectorToCssMap
}
